import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * A server-paginated, server-searched list: the one way to back a table,
 * search box or picker with an API list. See docs/frontend-lists-and-search.md.
 *
 * It owns the three things screens kept getting wrong:
 *   - search and filtering happen on the server, over every record, never on
 *     the page already in memory;
 *   - a new search or filter goes back to page 1;
 *   - typing is debounced, earlier requests are aborted, and a response that
 *     arrives after a newer one is ignored.
 */
export interface ServerPage<T> {
  items: T[];
  /** Total matching records, or null when the endpoint does not report one. */
  total: number | null;
  hasMore: boolean;
}

export interface ServerListQuery<F> {
  page: number;
  pageSize: number;
  /** Trimmed search text; '' when empty. */
  search: string;
  filters: F;
  signal: AbortSignal;
}

export interface UseServerListOptions<T, F> {
  fetchPage: (query: ServerListQuery<F>) => Promise<ServerPage<T>>;
  /** Server-side filters. Any change (by value) goes back to page 1. */
  filters?: F;
  pageSize?: number;
  debounceMs?: number;
  /** False skips fetching, e.g. while a dropdown is closed. */
  enabled?: boolean;
  initialSearch?: string;
}

export function useServerList<T, F = Record<string, never>>({
  fetchPage, filters, pageSize = 25, debounceMs = 250, enabled = true, initialSearch = ''
}: UseServerListOptions<T, F>) {
  const [search, setSearchState] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch.trim());
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ServerPage<T>>({ items: [], total: null, hasMore: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const seq = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const filtersKey = JSON.stringify(filters ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilters = useMemo(() => filters ?? ({} as F), [filtersKey]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  // A different question starts from the first page of its answer. Checked
  // inside the fetch effect so the old page is never requested for it.
  const questionKey = `${debouncedSearch}\u0000${filtersKey}`;
  const lastQuestion = useRef(questionKey);

  useEffect(() => {
    if (lastQuestion.current !== questionKey) {
      lastQuestion.current = questionKey;
      // Bumping seq also discards the in-flight answer to the old question.
      if (page !== 1) { seq.current++; setPage(1); return; }
    }
    if (!enabled) return;
    const mine = ++seq.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchRef.current({ page, pageSize, search: debouncedSearch, filters: stableFilters, signal: controller.signal })
      .then(next => { if (mine === seq.current) setResult(next); })
      .catch(err => {
        if (mine !== seq.current || controller.signal.aborted) return;
        setError(err?.message || 'Failed to load');
      })
      .finally(() => { if (mine === seq.current) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, pageSize, questionKey, reloadKey]);

  const setSearch = useCallback((value: string) => setSearchState(value), []);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);
  const totalPages = result.total !== null ? Math.max(1, Math.ceil(result.total / pageSize)) : null;

  return {
    items: result.items, total: result.total, hasMore: result.hasMore,
    page, setPage, pageSize, totalPages,
    search, setSearch, loading, error, reload,
  };
}
